import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, Layers, Video, Download, TrendingUp, 
  Instagram, Linkedin, Twitter, Youtube, Clock, Sparkles, LayoutTemplate
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PostStatusBadge } from '../components/PostStatusBadge';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { ThisWeekPreviewWidget } from '../components/ThisWeekPreviewWidget';

export const DashboardPage = () => {
  const { user, api } = useAuth();
  const [stats, setStats] = useState({
    total_posts: 0,
    saved_posts: 0,
    by_platform: {},
    by_type: {}
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, postsRes] = await Promise.all([
          api.get('/stats'),
          api.get('/posts?limit=5')
        ]);
        setStats(statsRes.data);
        setRecentPosts(postsRes.data.posts);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const getPlatformIcon = (platform) => {
    const icons = {
      Instagram: <Instagram className="w-4 h-4" />,
      LinkedIn: <Linkedin className="w-4 h-4" />,
      TikTok: <div className="w-4 h-4 text-xs font-bold">TT</div>,
      'Bluesky': <Twitter className="w-4 h-4" />,
      Twitter: <Twitter className="w-4 h-4" />,
      Facebook: <div className="w-4 h-4 text-xs font-bold">FB</div>,
      'YouTube Shorts': <Youtube className="w-4 h-4" />
    };
    return icons[platform] || <FileText className="w-4 h-4" />;
  };

  const quickActions = [
    { title: 'Generate Posts', icon: <FileText className="w-5 h-5" />, href: '/generate', color: 'bg-indigo-500' },
    { title: 'Use Template', icon: <LayoutTemplate className="w-5 h-5" />, href: '/templates', color: 'bg-violet-500' },
    { title: 'Video Script', icon: <Video className="w-5 h-5" />, href: '/generate?type=video', color: 'bg-pink-500' },
    { title: 'Export CSV', icon: <Download className="w-5 h-5" />, href: '/export', color: 'bg-emerald-500' }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-zinc-500 mt-1">Here's what's happening with your content</p>
        </div>
        <Link to="/generate">
          <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="generate-content-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            Generate Content
          </Button>
        </Link>
      </div>

      {/* Onboarding checklist — self-hides when done or dismissed */}
      <OnboardingChecklist />

      {/* Quick Actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {quickActions.map((action) => (
          <Link key={action.title} to={action.href}>
            <Card className="border-zinc-200 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer group">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${action.color} text-white flex items-center justify-center group-hover:scale-105 transition-transform`}>
                  {action.icon}
                </div>
                <span className="font-medium text-zinc-700 group-hover:text-zinc-900">{action.title}</span>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">Total Posts</p>
                <p className="text-3xl font-bold text-zinc-900 mt-1">{stats.total_posts}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">Saved Posts</p>
                <p className="text-3xl font-bold text-zinc-900 mt-1">{stats.saved_posts}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">Carousels</p>
                <p className="text-3xl font-bold text-zinc-900 mt-1">{stats.by_type?.carousel || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-violet-50 flex items-center justify-center">
                <Layers className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">Video Scripts</p>
                <p className="text-3xl font-bold text-zinc-900 mt-1">{stats.by_type?.video_script || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-pink-50 flex items-center justify-center">
                <Video className="w-6 h-6 text-pink-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* This week's preview — pulls the next 7 days of scheduled posts */}
      <ThisWeekPreviewWidget />

      {/* Recent Posts */}
      <Card className="border-zinc-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Recent Content</CardTitle>
            <Link to="/posts">
              <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700">
                View all
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-zinc-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : recentPosts.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500">No content generated yet</p>
              <Link to="/generate">
                <Button variant="link" className="text-indigo-600 mt-2">
                  Generate your first post
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentPosts.map((post) => (
                <Link key={post.id} to={`/posts`}>
                  <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-zinc-50 transition-colors group">
                    <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-500">
                      {getPlatformIcon(post.platform)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-zinc-900 truncate group-hover:text-indigo-600 transition-colors">
                          {post.post_title}
                        </p>
                        {(post.status === 'scheduled' || post.status === 'published' || post.status === 'failed') && (
                          <PostStatusBadge post={post} size="sm" showTime={false} />
                        )}
                      </div>
                      <p className="text-sm text-zinc-500 truncate">{post.caption?.substring(0, 60)}...</p>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400 text-sm">
                      <Clock className="w-4 h-4" />
                      {new Date(post.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
